import { useState, useEffect } from "react";
import {
    Wifi, Router, Activity, Thermometer, ShieldAlert,
    CheckCircle2, XCircle, Clock, MapPin, Info,
    Signal, Radio, Users
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";


interface ApAssetData {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    job_id: string;
    target_ip: string;
    status: string;
    vendor: string;
    device_type: string;
    manufacturer: string;
    device_name: string;
    system_info: string;
    wireless: string;
    system_resources: string;
    health: string;
    polled_at: string;
}

interface ApAssetDetailProps {
    assetId: number;
    client_id: string;
}

// Utility to safely parse JSON strings from the API
const safeParseJSON = (jsonString: string | null) => {
    if (!jsonString) return {};
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return {};
    }
};

export default function ApAssetDetail({ assetId, client_id }: ApAssetDetailProps) {
    const [data, setData] = useState<ApAssetData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchApDetails = async () => {
        if (!assetId) return;
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: assetId, client_id: client_id })
            };

            const response = await baseapi.get("/ap_asset_detail", { params });

            if (response.data.status && response.data.data && response.data.data.length > 0) {
                setData(response.data.data[0]);
            } else if (response.data.id) {
                // Fallback in case the API directly returns the object
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load AP details");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApDetails();
    }, [assetId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-gray-800/20 border border-slate-200 dark:border-gray-700/50 rounded-lg shadow-sm">
                <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mb-3"></div>
                <p className="text-[10px] font-black text-slate-600 dark:text-gray-200 uppercase tracking-[0.2em]">Loading...</p>
            </div>
        );
    }

    const sysInfo = safeParseJSON(data?.system_info || "");
    const wireless = safeParseJSON(data?.wireless || "");
    const health = safeParseJSON(data?.health || "");

    const isCritical = health?.status?.toUpperCase() === 'CRITICAL';
    const hasIssues = health?.issues && health.issues.length > 0;
    const stations = wireless?.connected_stations || [];

    return (
        <div className="w-full flex flex-col gap-3 text-slate-800 dark:text-gray-200">

            {/* Header / Identity Card */}
            {!data ? (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg p-3 shadow-sm flex items-center gap-3 opacity-60">
                    <div className="bg-slate-100 dark:bg-gray-500/10 p-2 rounded-md border border-slate-200 dark:border-gray-500/20">
                        <Router size={20} className="text-slate-400 dark:text-gray-500" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-600 dark:text-gray-400">AP Details Unavailable</h2>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-gray-500">Asset ID: {assetId}</p>
                    </div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg p-3 shadow-sm flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-500/10 p-2 rounded-md border border-blue-500/20">
                            <Router size={20} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide leading-tight">{data.device_name || "Unknown AP"}</h2>
                            <div className="flex items-center gap-2 mt-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">
                                <span className="flex items-center gap-1"><MapPin size={10} /> {data.target_ip}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-gray-600"></span>
                                <span className="text-blue-600 dark:text-blue-300">{data.vendor}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-gray-600"></span>
                                <span>{data.device_type}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-gray-600"></span>
                                <span>ID: {data.asset_id}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                        {data.status === 'success' ? (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase tracking-widest rounded">
                                <CheckCircle2 size={10} /> Online
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-[9px] font-black uppercase tracking-widest rounded">
                                <XCircle size={10} /> Sync Failed
                            </span>
                        )}
                        <span className="text-[9px] text-slate-500 dark:text-gray-400 font-semibold flex items-center gap-1">
                            <Clock size={8} /> {new Date(data.polled_at).toLocaleString()}
                        </span>
                    </div>
                </div>
            )}

            {/* Network Port Visualization */}
            {/* <div className="bg-gray-800/20 border border-gray-700/50 rounded-xl p-3">
                <ApPortVisualization 
                    assetId={assetId} 
                    onDataFetched={onInterfacesFetched} 
                    initialData={interfaces}
                    client_id={client_id}
                />
            </div> */}
            {hasIssues && (
                <div className={`p-2.5 rounded-lg border flex gap-2 items-start ${isCritical ? 'bg-rose-500/10 border-rose-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                    <ShieldAlert size={16} className={`mt-0.5 ${isCritical ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`} />
                    <div className="flex-1">
                        <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 leading-none ${isCritical ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {health.status} Alerts
                        </h4>
                        <ul className="space-y-1">
                            {health.issues.map((issue: string, idx: number) => (
                                <li key={idx} className="text-[10px] text-slate-700 dark:text-gray-300 font-medium bg-white/80 dark:bg-gray-900/50 px-2 py-1 rounded border border-slate-200 dark:border-gray-800 leading-tight">
                                    {issue}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Info Grids */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                {/* System Info */}
                {data && (
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg p-3.5 h-full shadow-sm">
                        <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-slate-200 dark:border-gray-700/50">
                            <Info size={14} className="text-cyan-600 dark:text-cyan-400" />
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest leading-none">System Info</h3>
                        </div>

                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Vendor</p>
                                    <p className="text-[11px] font-semibold text-slate-800 dark:text-gray-300 truncate" title={data.manufacturer}>{data.manufacturer || "-"}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Firmware</p>
                                    <span className="inline-block px-1.5 py-0.5 bg-slate-100 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded text-[9px] font-mono text-cyan-700 dark:text-cyan-300 leading-none">
                                        {wireless.software_version || "Unknown"}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Uptime</p>
                                <p className="text-[11px] font-semibold text-slate-800 dark:text-gray-300 flex items-center gap-1">
                                    <Activity size={10} className="text-emerald-500 dark:text-emerald-400" /> {sysInfo.uptime || "-"}
                                </p>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Description</p>
                                <p className="text-[9px] font-mono text-slate-700 dark:text-gray-200 bg-slate-50 dark:bg-gray-900/50 p-1.5 rounded border border-slate-200 dark:border-gray-800 truncate" title={sysInfo.description}>
                                    {sysInfo.description || "-"}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Wireless Setup */}
                {data && (
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg p-3.5 h-full shadow-sm">
                        <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-slate-200 dark:border-gray-700/50">
                            <Wifi size={14} className="text-purple-600 dark:text-purple-400" />
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest leading-none">Wireless Setup</h3>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-gray-900/50 rounded-md border border-slate-200 dark:border-gray-800">
                                <div>
                                    <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">SSID Network</p>
                                    <p className="text-xs font-black text-purple-700 dark:text-purple-300 leading-none">{wireless.ssid || "-"}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Mode</p>
                                    <p className="text-[10px] font-bold text-slate-800 dark:text-gray-300 leading-none">{wireless.device_mode || "-"}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div className="bg-slate-50 dark:bg-gray-900/30 p-2 rounded-md border border-slate-200 dark:border-gray-800">
                                    <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1 leading-none"><Signal size={8} /> Frequency</p>
                                    <p className="text-[10px] font-mono text-slate-800 dark:text-gray-300 leading-none">{wireless.radio?.frequency_mhz ? `${wireless.radio.frequency_mhz} MHz` : "-"}</p>
                                </div>
                                <div className="bg-slate-50 dark:bg-gray-900/30 p-2 rounded-md border border-slate-200 dark:border-gray-800">
                                    <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1 leading-none"><Thermometer size={8} /> Temp</p>
                                    <p className={`text-[10px] font-mono font-bold leading-none ${wireless.temperature_celsius > 75 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-800 dark:text-gray-300'}`}>
                                        {wireless.temperature_celsius ? `${wireless.temperature_celsius}°C` : "-"}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 pt-1">
                                <div>
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">TX Power</p>
                                    <p className="text-[10px] font-semibold text-slate-800 dark:text-gray-300">{wireless.radio?.tx_power_dbm ? `${wireless.radio.tx_power_dbm} dBm` : "-"}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Sessions</p>
                                    <p className="text-[10px] font-semibold text-slate-800 dark:text-gray-300">{wireless.sessions?.active_count || 0} Active</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Radio Metrics */}
                {data && (
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg p-3.5 h-full shadow-sm">
                        <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-slate-200 dark:border-gray-700/50">
                            <Radio size={14} className="text-emerald-600 dark:text-emerald-400" />
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest leading-none">Radio Metrics</h3>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-gray-900/50 rounded-md border border-slate-200 dark:border-gray-800">
                                <div>
                                    <p className="text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">Signal Quality</p>
                                    <p className="text-xs font-black capitalize text-emerald-600 dark:text-emerald-400 leading-none">{wireless.radio?.signal_quality || "-"}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 pt-1">
                                <div>
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">RSSI</p>
                                    <p className="text-[10px] font-mono font-bold text-slate-800 dark:text-gray-300">{wireless.radio?.rssi_dbm ? `${wireless.radio.rssi_dbm} dBm` : "-"}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-0.5 leading-none">SNR</p>
                                    <p className="text-[10px] font-mono font-bold text-slate-800 dark:text-gray-300">{wireless.radio?.snr_db ? `${wireless.radio.snr_db} dB` : "-"}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* Connected Stations Table */}
            {data && (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg p-3.5 shadow-sm">
                    <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-slate-200 dark:border-gray-700/50">
                        <div className="flex items-center gap-1.5">
                            <Users size={14} className="text-indigo-600 dark:text-indigo-400" />
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest leading-none">Connected Stations</h3>
                        </div>
                        <span className="text-[9px] font-bold bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-500/20 dark:border-indigo-500/30 leading-none">
                            {stations.length} Active
                        </span>
                    </div>

                    {stations.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-gray-900/50 border-y border-slate-200 dark:border-gray-700">
                                    <tr>
                                        <th className="px-2 py-1.5 text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">MAC Address</th>
                                        <th className="px-2 py-1.5 text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">Sig / SNR</th>
                                        <th className="px-2 py-1.5 text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">Qual / CCQ</th>
                                        <th className="px-2 py-1.5 text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">TX Rate</th>
                                        <th className="px-2 py-1.5 text-[8px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest text-right">Uptime</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-gray-700/50 text-xs">
                                    {stations.map((station: any, idx: number) => (
                                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-gray-800/60 transition-colors">
                                            <td className="px-2 py-1.5">
                                                <span className="text-[10px] font-mono text-indigo-600 dark:text-indigo-300 font-bold">{station.mac_address}</span>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span className="text-[9px] text-slate-700 dark:text-gray-300">{station.signal_dbm}</span>
                                                <span className="text-slate-400 dark:text-gray-500 mx-0.5">/</span>
                                                <span className="text-[9px] text-slate-700 dark:text-gray-300">{station.snr_db}</span>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span className={`text-[9px] font-bold capitalize ${station.signal_quality === 'good' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                                    {station.signal_quality}
                                                </span>
                                                <span className="text-slate-400 dark:text-gray-500 mx-0.5">/</span>
                                                <span className="text-[9px] text-slate-700 dark:text-gray-300">{station.ccq}%</span>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span className="px-1 py-0.5 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-600 rounded text-[9px] font-mono text-slate-700 dark:text-gray-300">
                                                    {station.tx_rate || "-"}
                                                </span>
                                            </td>
                                            <td className="px-2 py-1.5 text-right text-[9px] font-mono text-slate-500 dark:text-gray-400">
                                                {station.uptime}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-4 opacity-50 text-slate-400 dark:text-gray-400">
                            <Users size={18} className="mb-1" />
                            <p className="text-[9px] uppercase tracking-widest font-bold">No stations connected</p>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
}